import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, Trash2 } from "lucide-react";
import { Field, TextInput, TextArea, SectionTitle, Subtitle } from "./Field";
import { ImageField } from "./ImageField";
import { FileField } from "./FileField";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://smapi.sanmartino26.al'}/api`;

const TABS = [
  { id: "brand", label: "Marka (Logo)" },
  { id: "hero", label: "Hero" },
  { id: "intro", label: "Hyrja (Intro)" },
  { id: "team", label: "Ekipi" },
  { id: "chef", label: "Chef" },
  { id: "menu", label: "Menu" },
  { id: "tasting", label: "Degustimi" },
  { id: "gallery", label: "Galeria" },
  { id: "footer", label: "Footer" },
];

// ----- Editors -----

const BrandEditor = ({ data, set, token }) => (
  <div className="space-y-6">
    <SectionTitle sub="Përcaktoni logot e faqes (një e hapur për sfonde të errëta dhe një e errët për sfonde të hapura).">
      Marka
    </SectionTitle>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <ImageField
        label="Logo Kryesore (e hapur, për sfond të errët)"
        value={data.logo_url}
        onChange={(v) => set({ ...data, logo_url: v })}
        token={token}
        testid="content-brand-logo"
      />
      <ImageField
        label="Logo Sekondare (e errët, kur faqa bëhet e bardhë)"
        value={data.logo_url_dark}
        onChange={(v) => set({ ...data, logo_url_dark: v })}
        token={token}
        testid="content-brand-logo-dark"
      />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Field label="Lartësia Desktop (p.sh. 60px ose 4rem)">
        <TextInput
          value={data.logo_height_desktop}
          onChange={(v) => set({ ...data, logo_height_desktop: v })}
          placeholder="Default: 40px"
          testid="content-brand-logo-height-desktop"
        />
      </Field>
      <Field label="Lartësia Mobile (p.sh. 40px ose 2.5rem)">
        <TextInput
          value={data.logo_height_mobile}
          onChange={(v) => set({ ...data, logo_height_mobile: v })}
          placeholder="Default: 32px"
          testid="content-brand-logo-height-mobile"
        />
      </Field>
      <Field label="Hapësira Lart Desktop (Margin Top)">
        <TextInput
          value={data.logo_margin_top_desktop}
          onChange={(v) => set({ ...data, logo_margin_top_desktop: v })}
          placeholder="Default: 0px (p.sh. 10px)"
          testid="content-brand-logo-mt-desktop"
        />
      </Field>
      <Field label="Hapësira Lart Mobile (Margin Top)">
        <TextInput
          value={data.logo_margin_top_mobile}
          onChange={(v) => set({ ...data, logo_margin_top_mobile: v })}
          placeholder="Default: 0px (p.sh. 5px)"
          testid="content-brand-logo-mt-mobile"
        />
      </Field>
    </div>
    <Field label="Numri i WhatsApp (Për butonin e kontaktit të shpejtë në fund të faqes)">
      <TextInput
        value={data.whatsapp_number}
        onChange={(v) => set({ ...data, whatsapp_number: v })}
        placeholder="p.sh. +355691234567"
        testid="content-brand-whatsapp"
      />
    </Field>
  </div>
);

const HeroEditor = ({ data, set, token }) => (
  <div className="space-y-6">
    <SectionTitle sub="Pjesa e parë e faqes — imazhi i sfondit dhe titulli kryesor.">
      Hero
    </SectionTitle>
    <ImageField
      label="Imazhi i sfondit"
      value={data.image_url}
      onChange={(v) => set({ ...data, image_url: v })}
      token={token}
      testid="content-hero-image"
    />
    <ImageField
      label="Imazhi i Titullit (Opsionale, shfaqet mbi tekstin e titullit)"
      value={data.title_image_url}
      onChange={(v) => set({ ...data, title_image_url: v })}
      token={token}
      testid="content-hero-title-image"
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Field label="Lartësia Imazhit Titull (Desktop) p.sh. '120px'">
        <TextInput
          value={data.title_image_height_desktop}
          onChange={(v) => set({ ...data, title_image_height_desktop: v })}
          placeholder="Lëre bosh për madhësinë natyrale"
          testid="content-hero-title-height-desktop"
        />
      </Field>
      <Field label="Lartësia Imazhit Titull (Mobile) p.sh. '80px'">
        <TextInput
          value={data.title_image_height_mobile}
          onChange={(v) => set({ ...data, title_image_height_mobile: v })}
          placeholder="Lëre bosh për madhësinë natyrale"
          testid="content-hero-title-height-mobile"
        />
      </Field>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Field label="Eyebrow (mbititull)">
        <TextInput
          value={data.eyebrow}
          onChange={(v) => set({ ...data, eyebrow: v })}
          testid="content-hero-eyebrow"
        />
      </Field>
      <Field label="Subtitle">
        <TextInput
          value={data.subtitle}
          onChange={(v) => set({ ...data, subtitle: v })}
          testid="content-hero-subtitle"
        />
      </Field>
      <Field label="Titulli" full>
        <TextInput
          value={data.title}
          onChange={(v) => set({ ...data, title: v })}
          testid="content-hero-title"
        />
      </Field>
      <Field label="Përshkrimi" full>
        <TextArea
          value={data.copy}
          onChange={(v) => set({ ...data, copy: v })}
          rows={5}
          testid="content-hero-copy"
        />
      </Field>
      <Field label="CTA primare">
        <TextInput
          value={data.cta_primary}
          onChange={(v) => set({ ...data, cta_primary: v })}
          testid="content-hero-cta-primary"
        />
      </Field>
      <Field label="CTA sekondare">
        <TextInput
          value={data.cta_secondary}
          onChange={(v) => set({ ...data, cta_secondary: v })}
          testid="content-hero-cta-secondary"
        />
      </Field>
    </div>
  </div>
);

const IntroEditor = ({ data, set }) => (
  <div className="space-y-6">
    <SectionTitle sub="Sektori i prezantimit të filozofisë (poshtë hero).">Hyrja (Intro)</SectionTitle>
    <Field label="Titulli Kryesor (Header)" full>
      <TextInput value={data.title} onChange={(v) => set({ ...data, title: v })} testid="content-intro-title" />
    </Field>
    <Field label="Përshkrimi (Content text)" full>
      <TextArea rows={4} value={data.copy} onChange={(v) => set({ ...data, copy: v })} testid="content-intro-copy" />
    </Field>
  </div>
);

const TeamEditor = ({ data, set, token }) => (
  <div className="space-y-6">
    <SectionTitle sub="Sektori i ekipit (Pasion, energji dhe mikpritje).">Ekipi</SectionTitle>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Field label="Eyebrow">
        <TextInput value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="content-team-eyebrow" />
      </Field>
      <Field label="Titulli">
        <TextInput value={data.title} onChange={(v) => set({ ...data, title: v })} testid="content-team-title" />
      </Field>
      <Field label="Bio — paragrafi 1" full>
        <TextArea rows={5} value={data.bio_1} onChange={(v) => set({ ...data, bio_1: v })} testid="content-team-bio1" />
      </Field>
      <Field label="Bio — paragrafi 2" full>
        <TextArea rows={5} value={data.bio_2} onChange={(v) => set({ ...data, bio_2: v })} testid="content-team-bio2" />
      </Field>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <ImageField
        label="Imazhi 1 (Sipër/Para)"
        value={data.image_url_1}
        onChange={(v) => set({ ...data, image_url_1: v })}
        token={token}
        testid="content-team-image1"
      />
      <ImageField
        label="Imazhi 2 (Poshtë/Prapa)"
        value={data.image_url_2}
        onChange={(v) => set({ ...data, image_url_2: v })}
        token={token}
        testid="content-team-image2"
      />
    </div>
  </div>
);

const ChefEditor = ({ data, set, token }) => {
  const stats = data.stats || [];
  const updStat = (i, k, v) => {
    const next = stats.map((s, idx) => (idx === i ? { ...s, [k]: v } : s));
    set({ ...data, stats: next });
  };
  return (
    <div className="space-y-6">
      <SectionTitle sub="Sektori i shefit — biografia dhe portreti.">Chef</SectionTitle>
      <ImageField
        label="Portreti i shefit"
        value={data.image_url}
        onChange={(v) => set({ ...data, image_url: v })}
        token={token}
        testid="content-chef-image"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Eyebrow">
          <TextInput value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="content-chef-eyebrow" />
        </Field>
        <Field label="Roli">
          <TextInput value={data.role} onChange={(v) => set({ ...data, role: v })} testid="content-chef-role" />
        </Field>
        <Field label="Emri i shefit">
          <TextInput value={data.name} onChange={(v) => set({ ...data, name: v })} testid="content-chef-name" />
        </Field>
        <div />
        <Field label="Titulli — rreshti 1">
          <TextInput value={data.title_line_1} onChange={(v) => set({ ...data, title_line_1: v })} testid="content-chef-title1" />
        </Field>
        <Field label="Titulli — rreshti 2">
          <TextInput value={data.title_line_2} onChange={(v) => set({ ...data, title_line_2: v })} testid="content-chef-title2" />
        </Field>
        <Field label="Bio — paragrafi 1" full>
          <TextArea rows={5} value={data.bio_1} onChange={(v) => set({ ...data, bio_1: v })} testid="content-chef-bio1" />
        </Field>
        <Field label="Bio — paragrafi 2" full>
          <TextArea rows={5} value={data.bio_2} onChange={(v) => set({ ...data, bio_2: v })} testid="content-chef-bio2" />
        </Field>
      </div>

      <Subtitle>Statistika (3)</Subtitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="border border-sm-border p-4 space-y-3">
            <p className="text-[0.6rem] tracking-[0.28em] uppercase text-sm-muted">
              Stat #{i + 1}
            </p>
            <TextInput
              value={stats[i]?.value}
              onChange={(v) => updStat(i, "value", v)}
              placeholder="Vlera (p.sh. 12+)"
              testid={`content-chef-stat-${i}-value`}
            />
            <TextInput
              value={stats[i]?.label}
              onChange={(v) => updStat(i, "label", v)}
              placeholder="Etiketa"
              testid={`content-chef-stat-${i}-label`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const MenuEditor = ({ data, set, token }) => {
  const cats = data.categories || [];
  const updCat = (i, patch) =>
    set({ ...data, categories: cats.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const addItem = (catIdx) => {
    const cat = cats[catIdx];
    const items = [...(cat.items || []), { name: "", desc: "", price: "" }];
    updCat(catIdx, { items });
  };
  const removeItem = (catIdx, itemIdx) => {
    const items = (cats[catIdx].items || []).filter((_, j) => j !== itemIdx);
    updCat(catIdx, { items });
  };
  const updItem = (catIdx, itemIdx, k, v) => {
    const items = (cats[catIdx].items || []).map((it, j) => (j === itemIdx ? { ...it, [k]: v } : it));
    updCat(catIdx, { items });
  };

  return (
    <div className="space-y-8">
      <SectionTitle sub="Menuja À la Carte — kategori dhe pjata.">Menu À la Carte</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Eyebrow">
          <TextInput value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="content-menu-eyebrow" />
        </Field>
        <Field label="Titulli (À la Carte)">
          <TextInput value={data.title} onChange={(v) => set({ ...data, title: v })} testid="content-menu-title" />
        </Field>
        <Field label="Disclaimer (sezonale)" full>
          <TextArea rows={2} value={data.disclaimer} onChange={(v) => set({ ...data, disclaimer: v })} testid="content-menu-disclaimer" />
        </Field>
      </div>

      <div className="mt-6 mb-8 border border-sm-border p-6 bg-sm-bg">
        <Subtitle>Dokumentet (PDF)</Subtitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FileField
            label="Ngarko PDF për faqen e Menusë"
            value={data.pdf_url}
            onChange={(v) => set({ ...data, pdf_url: v })}
            token={token}
            testid="content-menu-pdf"
          />
          <FileField
            label="Ngarko PDF për Listën e Verërave"
            value={data.wine_pdf_url}
            onChange={(v) => set({ ...data, wine_pdf_url: v })}
            token={token}
            testid="content-menu-wine-pdf"
          />
        </div>
      </div>

      {cats.map((cat, ci) => (
        <div key={cat.id || ci} className="border border-sm-border p-6 bg-sm-bg" data-testid={`content-menu-cat-${cat.id}`}>
          <Subtitle>
            {cat.title || `Kategoria #${ci + 1}`}{" "}
            <span className="text-sm-muted text-sm font-sans italic">— {cat.subtitle}</span>
          </Subtitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Titulli">
              <TextInput value={cat.title} onChange={(v) => updCat(ci, { title: v })} />
            </Field>
            <Field label="Subtitle">
              <TextInput value={cat.subtitle} onChange={(v) => updCat(ci, { subtitle: v })} />
            </Field>
          </div>
          <div className="mt-4">
            <ImageField
              label="Imazhi i kategorisë"
              value={cat.image_url}
              onChange={(v) => updCat(ci, { image_url: v })}
              token={token}
              testid={`content-menu-cat-${cat.id}-image`}
            />
          </div>

          <p className="mt-6 text-[0.65rem] tracking-[0.28em] uppercase text-sm-muted">Pjatat</p>
          <div className="space-y-3 mt-3">
            {(cat.items || []).map((it, ii) => (
              <div
                key={ii}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 border border-sm-border p-4"
                data-testid={`content-menu-cat-${cat.id}-item-${ii}`}
              >
                <input
                  type="text"
                  value={it.name || ""}
                  onChange={(e) => updItem(ci, ii, "name", e.target.value)}
                  placeholder="Emri"
                  className="sm-admin-input md:col-span-3"
                />
                <input
                  type="text"
                  value={it.desc || ""}
                  onChange={(e) => updItem(ci, ii, "desc", e.target.value)}
                  placeholder="Përshkrimi"
                  className="sm-admin-input md:col-span-7"
                />
                <input
                  type="text"
                  value={it.price || ""}
                  onChange={(e) => updItem(ci, ii, "price", e.target.value)}
                  placeholder="Çmimi"
                  className="sm-admin-input md:col-span-1"
                />
                <button
                  type="button"
                  onClick={() => removeItem(ci, ii)}
                  className="md:col-span-1 inline-flex items-center justify-center border border-red-300 text-red-700 hover:bg-red-50 p-2"
                  title="Fshi"
                  data-testid={`content-menu-cat-${cat.id}-item-${ii}-remove`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addItem(ci)}
              data-testid={`content-menu-cat-${cat.id}-add`}
              className="inline-flex items-center gap-2 border border-dashed border-sm-olive text-sm-olive px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase hover:bg-sm-olive hover:text-sm-bg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Shto pjatë
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const TastingEditor = ({ data, set }) => {
  const courses = data.courses || [];
  const upd = (i, k, v) => {
    const next = courses.map((c, idx) => (idx === i ? { ...c, [k]: v } : c));
    set({ ...data, courses: next });
  };
  const add = () => set({ ...data, courses: [...courses, { idx: "", name: "", desc: "" }] });
  const remove = (i) => set({ ...data, courses: courses.filter((_, j) => j !== i) });

  return (
    <div className="space-y-6">
      <SectionTitle sub="Menu Degustimi — lëvizjet, çmimi dhe shoqërimi i verërave.">
        Menu Degustimi
      </SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Eyebrow">
          <TextInput value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="content-tasting-eyebrow" />
        </Field>
        <Field label="Titulli">
          <TextInput value={data.title} onChange={(v) => set({ ...data, title: v })} testid="content-tasting-title" />
        </Field>
        <Field label="Subtitle">
          <TextInput value={data.subtitle} onChange={(v) => set({ ...data, subtitle: v })} testid="content-tasting-subtitle" />
        </Field>
        <Field label="CTA">
          <TextInput value={data.cta_label} onChange={(v) => set({ ...data, cta_label: v })} testid="content-tasting-cta" />
        </Field>
        <Field label="Përshkrimi" full>
          <TextArea rows={4} value={data.description} onChange={(v) => set({ ...data, description: v })} testid="content-tasting-description" />
        </Field>
        <Field label="Etiketa e çmimit">
          <TextInput value={data.price_label} onChange={(v) => set({ ...data, price_label: v })} testid="content-tasting-price-label" />
        </Field>
        <Field label="Çmimi">
          <TextInput value={data.price} onChange={(v) => set({ ...data, price: v })} testid="content-tasting-price" />
        </Field>
        <Field label="Wine pairing — etiketa">
          <TextInput value={data.wine_pairing_label} onChange={(v) => set({ ...data, wine_pairing_label: v })} testid="content-tasting-wine-label" />
        </Field>
        <Field label="Wine pairing — çmimi">
          <TextInput value={data.wine_pairing_price} onChange={(v) => set({ ...data, wine_pairing_price: v })} testid="content-tasting-wine-price" />
        </Field>
      </div>

      <Subtitle>Lëvizjet</Subtitle>
      <div className="space-y-3">
        {courses.map((c, i) => (
          <div
            key={i}
            data-testid={`content-tasting-course-${i}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-3 border border-sm-border p-4"
          >
            <input
              type="text"
              value={c.idx || ""}
              onChange={(e) => upd(i, "idx", e.target.value)}
              placeholder="Nr."
              className="sm-admin-input md:col-span-1"
            />
            <input
              type="text"
              value={c.name || ""}
              onChange={(e) => upd(i, "name", e.target.value)}
              placeholder="Emri i pjatës"
              className="sm-admin-input md:col-span-4"
            />
            <input
              type="text"
              value={c.desc || ""}
              onChange={(e) => upd(i, "desc", e.target.value)}
              placeholder="Përshkrimi"
              className="sm-admin-input md:col-span-6"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`content-tasting-course-${i}-remove`}
              className="md:col-span-1 inline-flex items-center justify-center border border-red-300 text-red-700 hover:bg-red-50 p-2"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          data-testid="content-tasting-course-add"
          className="inline-flex items-center gap-2 border border-dashed border-sm-olive text-sm-olive px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase hover:bg-sm-olive hover:text-sm-bg transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Shto lëvizje
        </button>
      </div>
    </div>
  );
};

const GalleryEditor = ({ data, set, token }) => {
  const images = data.images || [];
  const updImg = (i, k, v) => {
    const next = images.map((img, idx) => (idx === i ? { ...img, [k]: v } : img));
    set({ ...data, images: next });
  };
  const add = () => set({ ...data, images: [...images, { url: "", alt: "" }] });
  const remove = (i) => set({ ...data, images: images.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6">
      <SectionTitle sub="Galeria e imazheve të ambientit dhe ushqimeve.">Galeria</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Eyebrow">
          <TextInput value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="content-gallery-eyebrow" />
        </Field>
        <Field label="Titulli">
          <TextInput value={data.title} onChange={(v) => set({ ...data, title: v })} testid="content-gallery-title" />
        </Field>
      </div>

      <Subtitle>Imazhet</Subtitle>
      <div className="space-y-4">
        {images.map((img, i) => (
          <div key={i} className="border border-sm-border p-4 space-y-4 bg-sm-bg">
            <div className="flex justify-between items-center">
              <span className="text-[0.6rem] tracking-[0.28em] uppercase text-sm-muted">Imazhi #{i + 1}</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <ImageField
              label="Ngarko Imazh"
              value={img.url}
              onChange={(v) => updImg(i, "url", v)}
              token={token}
            />
            <Field label="Përshkrimi (Alt text)">
              <TextInput value={img.alt} onChange={(v) => updImg(i, "alt", v)} />
            </Field>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-2 border border-dashed border-sm-olive text-sm-olive px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase hover:bg-sm-olive hover:text-sm-bg transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Shto Imazh
        </button>
      </div>
    </div>
  );
};

const FooterEditor = ({ data, set }) => (
  <div className="space-y-6">
    <SectionTitle sub="Sektori i kontaktit & adresës në fund të faqes.">Footer</SectionTitle>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Field label="Eyebrow">
        <TextInput value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="content-footer-eyebrow" />
      </Field>
      <Field label="Titulli">
        <TextInput value={data.title} onChange={(v) => set({ ...data, title: v })} testid="content-footer-title" />
      </Field>
      <Field label="Adresa — rreshti 1">
        <TextInput value={data.address_line_1} onChange={(v) => set({ ...data, address_line_1: v })} testid="content-footer-addr1" />
      </Field>
      <Field label="Adresa — rreshti 2">
        <TextInput value={data.address_line_2} onChange={(v) => set({ ...data, address_line_2: v })} testid="content-footer-addr2" />
      </Field>
      <Field label="Orari — drekë">
        <TextInput value={data.hours_lunch} onChange={(v) => set({ ...data, hours_lunch: v })} testid="content-footer-hours-lunch" />
      </Field>
      <Field label="Orari — darkë">
        <TextInput value={data.hours_dinner} onChange={(v) => set({ ...data, hours_dinner: v })} testid="content-footer-hours-dinner" />
      </Field>
      <Field label="Telefon">
        <TextInput value={data.phone} onChange={(v) => set({ ...data, phone: v })} testid="content-footer-phone" />
      </Field>
      <Field label="Email">
        <TextInput value={data.email} onChange={(v) => set({ ...data, email: v })} testid="content-footer-email" />
      </Field>
      <Field label="Instagram URL">
        <TextInput value={data.instagram_url} onChange={(v) => set({ ...data, instagram_url: v })} testid="content-footer-instagram" />
      </Field>
      <Field label="Facebook URL">
        <TextInput value={data.facebook_url} onChange={(v) => set({ ...data, facebook_url: v })} testid="content-footer-facebook" />
      </Field>
      <Field label="Map query (Google Maps)" full>
        <TextInput value={data.map_query} onChange={(v) => set({ ...data, map_query: v })} testid="content-footer-map" />
      </Field>
      <Field label="Tagline (italiku në fund)" full>
        <TextInput value={data.tagline} onChange={(v) => set({ ...data, tagline: v })} testid="content-footer-tagline" />
      </Field>
    </div>
  </div>
);

// ----- Main component -----
export const ContentEditor = ({ token }) => {
  const [tab, setTab] = useState("hero");
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/content`);
      delete data.updated_at;
      setContent(data);
      setDirty(false);
    } catch {
      toast.error("Gabim në ngarkimin e përmbajtjes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const setSection = (key) => (next) => {
    setContent((c) => ({ ...c, [key]: next }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        brand: content.brand || {},
        hero: content.hero || {},
        intro: content.intro || {},
        team: content.team || {},
        chef: content.chef || {},
        menu: content.menu || {},
        tasting: content.tasting || {},
        gallery: content.gallery || {},
        footer: content.footer || {},
      };
      await axios.put(`${API}/admin/content`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Përmbajtja u ruajt.");
      setDirty(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errMsg = typeof detail === "string" ? detail : "Gabim në ruajtje. Ju lutem kontrolloni të dhënat.";
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Riktheni përmbajtjen tek vlerat fillestare? Ndryshimet do të humbasin.")) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/content/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      delete data.updated_at;
      setContent(data);
      setDirty(false);
      toast.success("U rikthye tek vlerat fillestare.");
    } catch {
      toast.error("Gabim.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) {
    return <p className="text-sm-muted py-20 text-center">Duke ngarkuar përmbajtjen...</p>;
  }

  return (
    <div data-testid="admin-content-editor">
      {/* Sticky action bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-sm-border">
        <div className="flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`content-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase border transition-colors ${tab === t.id
                ? "border-sm-text bg-sm-text text-sm-bg"
                : "border-sm-border text-sm-muted hover:border-sm-olive hover:text-sm-olive"
                }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {dirty && (
            <span
              data-testid="content-dirty-indicator"
              className="text-[0.65rem] tracking-[0.28em] uppercase text-sm-terra"
            >
              · Pa ruajtur
            </span>
          )}
          <button
            type="button"
            onClick={reset}
            disabled={saving}
            data-testid="content-reset-btn"
            className="inline-flex items-center gap-2 border border-sm-border px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase text-sm-muted hover:text-sm-terra hover:border-sm-terra transition-colors disabled:opacity-60"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reseto
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="content-save-btn"
            className="inline-flex items-center gap-2 bg-sm-text text-sm-bg px-5 py-2 text-[0.7rem] tracking-[0.28em] uppercase font-medium hover:bg-sm-olive transition-colors disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Duke ruajtur..." : "Ruaj"}
          </button>
        </div>
      </div>

      <div className="bg-sm-surface border border-sm-border p-6 md:p-10">
        {tab === "brand" && (
          <BrandEditor data={content.brand || {}} set={setSection("brand")} token={token} />
        )}
        {tab === "hero" && (
          <HeroEditor data={content.hero || {}} set={setSection("hero")} token={token} />
        )}
        {tab === "intro" && (
          <IntroEditor data={content.intro || {}} set={setSection("intro")} />
        )}
        {tab === "team" && (
          <TeamEditor data={content.team || {}} set={setSection("team")} token={token} />
        )}
        {tab === "chef" && (
          <ChefEditor data={content.chef || {}} set={setSection("chef")} token={token} />
        )}
        {tab === "menu" && (
          <MenuEditor data={content.menu || {}} set={setSection("menu")} token={token} />
        )}
        {tab === "tasting" && (
          <TastingEditor data={content.tasting || {}} set={setSection("tasting")} />
        )}
        {tab === "gallery" && (
          <GalleryEditor data={content.gallery || {}} set={setSection("gallery")} token={token} />
        )}
        {tab === "footer" && (
          <FooterEditor data={content.footer || {}} set={setSection("footer")} />
        )}
      </div>
    </div>
  );
};

export default ContentEditor;
